// "Discussion" tab of the agent drawer (SPEC §17): history (GET /api/agents/:id/chat), bubbles
// (user right, agent left in Markdown), typing indicator while the agent answers, 409 when a
// reply is already running. Messages are sent asynchronously (POST {message, async: true} → 202
// {message, pending: true}); the reply arrives as SSE 'chat' {agentId, message} or
// {agentId, error}, and the history is polled every 5 s while it is pending (live link down).
// An older server answering 200 {reply, message} is still understood.
// "Transformer en tâche" on an agent message or on the last user message (POST /api/tasks from
// the chat), clear history (DELETE) with a confirmation. When some of the agent's connectors are
// outside the person's « Connecteurs autorisés », a note says it works without them here. When the
// server lets the office's administrators read the discussions (bootstrap
// features.chatVisibleToAdmins, OFFICE_ADMIN_CHAT_VIEW), a muted line with a lock says so to a member
// (its « ? »: the messages sent since the setting was turned on).
// Enter sends, Shift+Enter makes a new line. The composer lives in the drawer footer.
// Images (pièces jointes, 2 per message): 📎, a pasted screenshot or a dropped file join the next
// message (sent as attachments [{name, data}]); a message may carry images only. They show in its
// bubble (click → lightbox) and go with a task launched from it (chatFiles, or the composer's own
// images as attachments).
import { h, icon, button, emptyState, spinner, textarea, field, switchToggle, setText, infoTip } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { isConflict } from '../../api.js';
import { renderMarkdown, relativeTime } from '../../format.js';
import { autosize, openLightbox } from '../tasks/components.js';
import { applyTaskResponse } from '../tasks/approvalbox.js';
import { createImagePicker, thumb, UPLOAD_TIMEOUT_MS } from '../attachments.js';
import { agentAvatar } from './avatar.js';
import { canUseConnector, keyLabel } from '../connectorpolicy.js';
import {
  LIMITS, charLength, normalizeChatHistory, normalizeChatResponse, lastUserIndex, canChooseAuto, chatTaskBody,
  taskTextFromChat, engineLabel, chatFileUrl, chatFilesForTask,
} from './model.js';
import './strings.js';

/** Longest wait for a chat reply (the engine's own timeout is 15 min). */
export const CHAT_TIMEOUT_MS = 16 * 60 * 1000;
/** How often the history is checked while a reply is pending (fallback for a lost live event). */
export const CHAT_POLL_MS = 5000;
/** The async POST only stores the message: it answers at once (202), so a short timeout is enough. */
export const CHAT_POST_TIMEOUT_MS = 30 * 1000;
/** …unless it carries images (up to 2 × 10 Mo). */
export const CHAT_UPLOAD_TIMEOUT_MS = UPLOAD_TIMEOUT_MS;
/** Longest task request (config limits.maxTaskInput default). */
const MAX_TASK_INPUT = 20000;

/**
 * True when the key event should send the message (Enter without Shift, not while composing).
 * @param {{key: string, shiftKey?: boolean, isComposing?: boolean, altKey?: boolean, ctrlKey?: boolean, metaKey?: boolean}} e
 */
export function isSendKey(e) {
  return e?.key === 'Enter' && !e.shiftKey && !e.isComposing && !e.altKey;
}

/**
 * Builds the tab.
 * @param {object} ctx app context
 * @param {object} agent
 * @param {{canChat: boolean, canManage?: boolean, scroller?: HTMLElement}} opts scroller: the element that scrolls (layer body)
 * @returns {{el: HTMLElement, footer: HTMLElement, activate(): void, deactivate(): void, destroy(): void}}
 */
export function createChatTab(ctx, agent, { canChat, canManage = false, scroller = null } = {}) {
  const path = `/api/agents/${encodeURIComponent(agent.id)}/chat`;
  let messages = [];
  let loaded = false;
  let loading = false;
  let unavailable = false;
  let forbidden = !canChat;
  let sending = false;   // the POST is in flight
  let awaiting = false;  // the reply is pending on the server (async send, or another tab)
  let awaitSince = 0;
  let destroyed = false;
  let active = false; // the tab is shown (its delayed focus must not steal another tab's)
  let reloadTimer = null;
  let pollTimer = null;
  const aborter = typeof AbortController === 'function' ? new AbortController() : null;
  const offs = [];

  // --- history ---------------------------------------------------------------------------------
  const clearBtn = button(t('ad.chat.clear'), { variant: 'ghost', size: 'sm', icon: 'close', ariaLabel: t('ad.chat.clearAria'), onClick: () => clearHistory() });
  clearBtn.hidden = true;
  const intro = h('p', { class: 'ad-chat-intro' }, icon('info', { size: 14 }), h('span', {}, t('ad.chat.intro', { name: agent.name, engine: engineLabel(agent.engine) })));
  const log = h('div', { class: 'ad-chat-log', role: 'log', 'aria-live': 'polite', 'aria-label': t('ad.chat.logLabel') });
  const typing = h('div', { class: 'ad-row is-agent ad-typing', hidden: true },
    agentAvatar(agent, { size: 28 }),
    h('div', { class: 'ad-bubble is-agent is-typing' },
      h('span', { class: 'ad-dots', 'aria-hidden': 'true' }, h('span', {}), h('span', {}), h('span', {})),
      h('span', { class: 'sr-only' }, t('ad.chat.typing', { name: agent.name }))));
  const state = h('div', { class: 'ad-chat-state' });
  // The agent's connectors outside my « Connecteurs autorisés »: it answers me without them.
  const store = ctx.store.get();
  const denied = [...new Set((Array.isArray(agent.connectors) ? agent.connectors : [])
    .map((g) => g?.server).filter((k) => typeof k === 'string' && k && !canUseConnector(store, k))
    .map((k) => keyLabel(k, store.connectors || [])))];
  const deniedNote = denied.length
    ? h('p', { class: 'ad-chat-denied', role: 'note' }, icon('shield', { size: 14 }), h('span', {}, t('ad.chat.deniedNote', { name: agent.name, list: denied.join(', ') })))
    : null;
  // The administrators may read this discussion (Administration → Activité): a member is told,
  // the « ? » says which messages (the full sentence would not fit one line).
  const privacyNote = store.features?.chatVisibleToAdmins === true && store.me?.role !== 'admin'
    ? h('p', { class: 'ad-chat-privacy', role: 'note' }, icon('lock', { size: 13 }), h('span', {}, t('ad.chat.adminsCanRead')),
      infoTip(t('ad.chat.adminsCanReadTip')))
    : null;
  const el = h('div', { class: 'ad-chat' }, h('div', { class: 'ad-chat-bar' }, intro, clearBtn), privacyNote, deniedNote, state, log, typing);

  // --- composer --------------------------------------------------------------------------------
  const area = textarea({
    class: 'ad-composer-input', rows: 1, placeholder: t('ad.chat.placeholder', { name: agent.name }),
    'aria-label': t('ad.chat.inputLabel', { name: agent.name }), maxLength: LIMITS.chat + 200,
  });
  const fit = autosize(area, { max: 200 });
  const counter = h('span', { class: 'ad-composer-count', 'aria-hidden': 'true' });
  const sendBtn = h('button', { type: 'submit', class: 'btn btn-primary ad-send', 'aria-label': t('ad.chat.send') },
    icon('send', { size: 16 }), h('span', { class: 'btn-label' }, t('ad.chat.send')));
  const error = h('p', { class: 'ad-composer-error', role: 'alert', hidden: true });
  const hint = h('p', { class: 'ad-composer-hint' }, t('ad.chat.hint'));
  // Images joined to the next message (📎, paste, drop): 2 at most.
  const picker = createImagePicker({
    onChange: () => { setError(null); syncComposer(); },
    onError: (msg) => setError(msg),
  });
  // « Lancer comme tâche »: the composer's text (or the last message) becomes a task, prefilled
  // (agent, department, text) in the task form; a standing hint says why (a discussion never acts).
  // The composer's images go with its text; the last message's images go with it.
  const launchBtn = h('button', {
    type: 'button', class: 'btn btn-secondary btn-sm ad-launch', title: t('ad.chat.launchTitle', { name: agent.name }),
    onClick: () => {
      const draft = String(area.value ?? '').trim();
      const lastUser = lastUserIndex(messages);
      const joined = picker.count() > 0;
      if (!draft && !joined && lastUser < 0) { setError(t('ad.chat.launchEmpty')); area.focus?.(); return; }
      openToTask(lastUser, draft || joined ? { text: draft, composer: true } : {});
    },
  }, icon('bolt', { size: 13 }), h('span', { class: 'btn-label' }, t('ad.chat.launch')));
  const workRow = h('div', { class: 'ad-composer-work' },
    h('p', { class: 'ad-composer-work-hint' }, t('ad.chat.workHint')), launchBtn);
  const footer = h('form', { class: 'ad-composer', novalidate: true, onSubmit: (e) => { e.preventDefault(); send(); } },
    picker.tray,
    h('div', { class: 'ad-composer-row' }, picker.el, area, sendBtn), h('div', { class: 'ad-composer-foot' }, hint, counter), workRow, error);
  picker.attachPaste(area);
  picker.attachDrop(footer);

  area.addEventListener('keydown', (e) => {
    if (isSendKey(e)) { e.preventDefault(); send(); }
  });
  area.addEventListener('input', () => { setError(null); syncComposer(); });

  function setError(msg) {
    setText(error, msg || '');
    error.hidden = !msg;
  }

  function syncComposer() {
    const value = String(area.value ?? '');
    const n = charLength(value);
    setText(counter, n > LIMITS.chat * 0.8 ? t('ad.chat.count', { n, max: LIMITS.chat }) : '');
    counter.classList.toggle('is-over', n > LIMITS.chat);
    const blocked = forbidden || unavailable;
    area.disabled = blocked;
    picker.setDisabled(blocked);
    workRow.hidden = forbidden;
    sendBtn.disabled = blocked || sending || awaiting || (!value.trim() && !picker.count()) || n > LIMITS.chat;
    sendBtn.classList.toggle('is-busy', sending || awaiting);
    hint.textContent = forbidden ? t('ad.chat.forbidden') : unavailable ? t('ad.chat.unavailable') : t('ad.chat.hint');
  }

  /** The images of a message: their URLs on the server, or the previews of a message being sent. */
  function imagesOf(m) {
    if (Array.isArray(m.localFiles) && m.localFiles.length) return m.localFiles;
    return (Array.isArray(m.files) ? m.files : []).map((f) => ({ name: f.name, size: f.size, url: chatFileUrl(agent.id, f.name) }));
  }

  function bubbleImages(m) {
    const list = imagesOf(m);
    if (!list.length) return null;
    const viewable = list.filter((f) => f.url);
    return h('div', { class: 'ad-bubble-images' }, list.map((f) => h('button', {
      type: 'button', class: 'ad-bubble-img', 'aria-label': t('ad.chat.openImage', { name: f.name }), title: f.name,
      onClick: () => { const i = viewable.indexOf(f); if (i >= 0) openLightbox(ctx, viewable, i); },
    }, thumb(f, 'ad-bubble-pic'))));
  }

  function scrollDown() {
    const s = scroller;
    if (!s) return;
    const go = () => { s.scrollTop = s.scrollHeight; };
    if (typeof globalThis.requestAnimationFrame === 'function') globalThis.requestAnimationFrame(go); else go();
  }

  function bubble(m, index, lastUser) {
    const mine = m.role === 'user';
    const at = m.at ? new Date(typeof m.at === 'number' ? m.at : Date.parse(m.at)) : null;
    const valid = at && Number.isFinite(at.getTime());
    const offerTask = canChat && !m.pending && !m.taskId && (m.role === 'agent' || index === lastUser);
    const images = mine ? bubbleImages(m) : null;
    return h('div', { class: ['ad-row', mine ? 'is-user' : 'is-agent', { 'is-pending': !!m.pending }] },
      mine ? null : agentAvatar(agent, { size: 28 }),
      h('div', { class: 'ad-bubble-wrap' },
        h('div', { class: ['ad-bubble', mine ? 'is-user' : 'is-agent', { 'has-images': !!images }] },
          images,
          mine ? (m.text ? h('p', { class: 'ad-bubble-text' }, m.text) : null) : h('div', { class: 'md ad-bubble-md' }, renderMarkdown(m.text))),
        h('div', { class: 'ad-bubble-meta' },
          h('span', { class: 'ad-bubble-who' }, mine ? t('ad.chat.you') : agent.name),
          valid ? h('time', { class: 'ad-bubble-time', datetime: at.toISOString() }, relativeTime(at, Date.now())) : null,
          offerTask ? h('button', {
            type: 'button', class: 'btn btn-link btn-sm ad-to-task', 'aria-label': `${t('ad.toTask.onMessage')} (${mine ? t('ad.chat.you') : agent.name})`,
            onClick: () => openToTask(index),
          }, icon('bolt', { size: 13 }), h('span', { class: 'btn-label' }, t('agentdrawer.toTask'))) : null,
          m.taskId ? h('button', {
            type: 'button', class: 'btn btn-link btn-sm ad-task-link',
            onClick: () => ctx.navigate(`#/task/${encodeURIComponent(m.taskId)}`),
          }, h('span', { class: 'btn-label' }, t('ad.chat.taskLink')), icon('arrowRight', { size: 13 })) : null)));
  }

  function renderLog() {
    if (destroyed) return;
    const lastUser = lastUserIndex(messages);
    log.replaceChildren(...messages.map((m, i) => bubble(m, i, lastUser)));
    clearBtn.hidden = !messages.length || forbidden || unavailable;
    let st = null;
    if (forbidden) st = h('p', { class: 'ad-notice', role: 'note' }, t('ad.chat.forbidden'));
    else if (unavailable) st = h('p', { class: 'ad-notice', role: 'note' }, t('ad.chat.unavailable'));
    else if (loading && !loaded) st = h('div', { class: 'ad-tab-loading' }, spinner(t('ad.chat.loading')), h('span', { class: 'muted' }, t('ad.chat.loading')));
    else if (loaded && !messages.length && !sending && !awaiting) {
      st = emptyState({ icon: 'send', title: t('ad.chat.empty.title', { name: agent.name }), text: t('ad.chat.empty.text') });
    }
    state.replaceChildren(...(st ? [st] : []));
    syncComposer();
  }

  function showTyping(on) {
    typing.hidden = !on;
    log.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  /** Enters or leaves the "reply pending" state (typing indicator + history polling). */
  function setAwaiting(on) {
    if (on && !awaiting) awaitSince = Date.now();
    awaiting = !!on;
    clearTimeout(pollTimer);
    pollTimer = null;
    if (awaiting && !destroyed) pollTimer = setTimeout(poll, CHAT_POLL_MS);
    showTyping(awaiting || sending);
  }

  /** Fallback while a reply is pending: read the history again (the live event may be lost). */
  async function poll() {
    pollTimer = null;
    if (destroyed || !awaiting) return;
    if (Date.now() - awaitSince > CHAT_TIMEOUT_MS) {
      setAwaiting(false);
      setError(t('ad.chat.noReply', { name: agent.name }));
      renderLog();
      return;
    }
    await load({ quiet: true });
    if (!destroyed && awaiting && !pollTimer) pollTimer = setTimeout(poll, CHAT_POLL_MS);
  }

  /** Adds a message published live unless the history already holds it. */
  function appendLive(raw) {
    const m = normalizeChatHistory([raw])[0];
    if (!m) return false;
    const at = typeof m.at === 'number' ? m.at : Date.parse(m.at);
    const dup = messages.some((x) => x.role === m.role && x.text === m.text
      && (typeof x.at === 'number' ? x.at : Date.parse(x.at)) === at);
    if (!dup) messages = [...messages, m];
    return true;
  }

  async function load({ quiet = false } = {}) {
    if (forbidden || loading || destroyed) { renderLog(); return; }
    loading = true;
    if (!quiet) renderLog();
    const before = messages.length;
    try {
      const res = await ctx.api.get(path);
      if (destroyed) return;
      if (!sending) {
        messages = normalizeChatHistory(res);
        // A reply is still running (this send, another tab, or a drawer reopened meanwhile).
        const pending = !!(res && typeof res === 'object' && (res.pending === true || res.busy === true));
        if (awaiting && !pending) {
          // Finished while the live event was missed; no reply after our message means it failed.
          const last = messages[messages.length - 1];
          if (last && last.role === 'user') setError(t('ad.chat.noReply', { name: agent.name }));
        }
        setAwaiting(pending);
      }
      unavailable = false;
    } catch (err) {
      if (destroyed) return;
      if (err?.status === 404) { unavailable = true; setAwaiting(false); }
      else if (err?.status === 403) { forbidden = true; setAwaiting(false); }
      else if (!quiet) ctx.toast?.error?.(err);
    } finally {
      loading = false;
    }
    loaded = true;
    renderLog();
    if (!quiet || messages.length !== before) scrollDown();
  }

  async function send() {
    const text = String(area.value ?? '').trim();
    if ((!text && !picker.count()) || sending || forbidden || unavailable || destroyed) return;
    if (charLength(text) > LIMITS.chat) { setError(t('ad.chat.tooLong', { max: LIMITS.chat })); return; }
    sending = true;
    setError(null);
    // The images leave the composer with the message (they come back if it is not sent).
    const taken = picker.take();
    const pending = { role: 'user', text, at: Date.now(), pending: true, localFiles: taken.map(({ name, url }) => ({ name, url })) };
    messages = [...messages, pending];
    area.value = '';
    fit();
    showTyping(true);
    renderLog();
    scrollDown();
    let async = false;
    let sent = false;
    try {
      const attachments = taken.length ? await picker.payload(taken) : null;
      if (destroyed) return;
      const body = attachments ? { message: text, async: true, attachments } : { message: text, async: true };
      const res = await ctx.api.post(path, body, { timeoutMs: attachments ? CHAT_UPLOAD_TIMEOUT_MS : CHAT_POST_TIMEOUT_MS, signal: aborter?.signal });
      if (destroyed) return;
      sent = true;
      if (res && typeof res === 'object' && res.pending === true) {
        // 202: the message is stored, the reply follows as a live 'chat' event.
        async = true;
        const user = normalizeChatHistory([res.message])[0];
        messages = [...messages.filter((m) => m !== pending), user ? { ...user, role: 'user' } : { role: 'user', text, at: pending.at }];
      } else {
        const r = normalizeChatResponse(res, text);
        messages = r.history || [...messages.filter((m) => m !== pending), r.user, ...(r.agent ? [r.agent] : [])];
      }
    } catch (err) {
      if (destroyed) return;
      messages = messages.filter((m) => m !== pending);
      if (!String(area.value ?? '').trim()) area.value = text;
      // A 409 staleClient (page older than the build served) is no running reply: its own message.
      if (isConflict(err)) setError(t('ad.chat.busy'));
      else if (err?.status === 404) unavailable = true;
      else if (err?.status === 403) { forbidden = true; setError(err.message || t('ad.chat.forbidden')); }
      else setError(t('ad.chat.failed', { error: err?.message || t('common.error') }));
    } finally {
      if (!destroyed) {
        sending = false;
        setAwaiting(async);
        fit();
        renderLog();
        scrollDown();
        // Sent: the previews give way to the server's images; not sent: back in the composer
        // (unless others were joined meanwhile), with the error.
        if (sent) picker.release(taken);
        else {
          const message = error.hidden ? '' : error.textContent;
          picker.restore(taken);
          if (message) setError(message);
        }
        if (!forbidden && !unavailable) area.focus?.();
      } else {
        picker.release(taken);
      }
    }
  }

  async function clearHistory() {
    const ok = await ctx.overlay.confirm({
      title: t('ad.chat.clearTitle'), message: t('ad.chat.clearText', { name: agent.name }),
      confirmLabel: t('ad.chat.clearOk'), danger: true,
    });
    if (!ok || destroyed) return;
    try {
      await ctx.api.del(path);
      if (destroyed) return;
      messages = [];
      renderLog();
      ctx.toast?.success?.(t('ad.chat.cleared'));
    } catch (err) {
      if (!destroyed) ctx.toast?.error?.(err);
    }
  }

  /**
   * « Transformer en tâche » / « Lancer comme tâche »: the task form, prefilled with the message
   * (or the composer's text: `composer`) and the images that go with it — the composer's own, or
   * those of the message (or of the user's message an agent message answers); × leaves one out.
   */
  function openToTask(index, { text: prefill, composer = false } = {}) {
    const scope = { floorId: agent.floorId, deptId: agent.deptId };
    const allowAuto = canChooseAuto({ canManage: canManage || ctx.can('manage', scope), agent });
    let approve = agent.autonomy !== 'auto';
    const text = h('textarea', {
      class: 'input textarea ad-totask-text', rows: 6, value: prefill ?? taskTextFromChat(messages, index), maxLength: MAX_TASK_INPUT, autofocus: true,
    });
    const fitTask = autosize(text, { max: 360, min: 120 });
    const f = field(t('ad.toTask.label', { name: agent.name }), text, { hint: t('ad.toTask.hint') });
    const composerIds = composer ? picker.items().map((it) => it.id) : [];
    let images = composer
      ? picker.items().map((it) => ({ name: it.name, url: it.url, local: it }))
      : chatFilesForTask(messages, index).map((file) => ({ name: file.name, url: chatFileUrl(agent.id, file.name), chat: file.name }));
    const imagesList = h('ul', { class: 'ad-totask-images', 'aria-label': t('ad.toTask.images') });
    const imagesBlock = h('div', { class: 'ad-totask-imgs' },
      h('p', { class: 'mono-label ad-totask-imgs-title' }, t('ad.toTask.images')), imagesList,
      h('p', { class: 'ad-totask-imgs-hint' }, t('ad.toTask.imagesHint')));
    function renderImages() {
      imagesList.replaceChildren(...images.map((img) => h('li', { class: 'att-item', title: img.name },
        thumb(img),
        h('span', { class: 'att-name' }, img.name),
        h('button', {
          type: 'button', class: 'att-remove', 'aria-label': t('ad.toTask.removeImage', { name: img.name }), title: t('ad.toTask.removeImage', { name: img.name }),
          onClick: () => { images = images.filter((x) => x !== img); renderImages(); },
        }, icon('close', { size: 12 })))));
      imagesBlock.hidden = !images.length;
    }
    renderImages();
    const modeHint = h('p', { class: 'ad-totask-mode' });
    const sw = switchToggle(t('ad.toTask.approve'), {
      checked: approve, disabled: !allowAuto,
      onChange: (v) => { approve = v; syncMode(); },
    });
    function syncMode() {
      setText(modeHint, !allowAuto ? t('ad.toTask.autoLocked') : approve ? t('ad.toTask.approveHint') : t('ad.toTask.autoHint'));
    }
    syncMode();
    let busy = false;
    const submit = button(t('ad.toTask.submit'), { variant: 'primary', icon: 'bolt' });
    const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => modal.close('cancel') });
    async function onSubmit(e) {
      e?.preventDefault?.();
      if (busy) return;
      const value = String(text.value ?? '').trim();
      if (!value) { f.setError(t('ad.toTask.required')); text.focus?.(); return; }
      if (value.length > MAX_TASK_INPUT) { f.setError(t('ad.toTask.tooLong', { max: MAX_TASK_INPUT })); return; }
      f.setError(null);
      busy = true;
      submit.disabled = true;
      submit.classList.add('is-busy');
      try {
        const local = images.filter((img) => img.local).map((img) => img.local);
        const chatFiles = images.filter((img) => img.chat).map((img) => img.chat);
        const attachments = local.length ? await picker.payload(local) : [];
        const res = await ctx.api.post('/api/tasks', chatTaskBody({ agent, text: value, approve: allowAuto ? approve : true, chatFiles, attachments }),
          attachments.length ? { timeoutMs: CHAT_UPLOAD_TIMEOUT_MS } : undefined);
        const summary = applyTaskResponse(ctx, res);
        const taskId = summary?.id || (typeof res?.id === 'string' ? res.id : null);
        // The composer's text became the task: clear it (a message typed afterwards stays); its
        // images went with it unless others were joined meanwhile.
        if (prefill !== undefined && String(area.value ?? '').trim() === prefill.trim()) { area.value = ''; fit(); syncComposer(); }
        if (composer && composerIds.length && picker.items().map((it) => it.id).join() === composerIds.join()) picker.clear();
        modal.close('confirm');
        load(); // the server notes the new task in the discussion (a message linking to it)
        ctx.toast?.success?.(t('ad.toTask.created', { name: agent.name }), taskId ? {
          action: { label: t('ad.toTask.open'), onClick: () => ctx.navigate(`#/task/${encodeURIComponent(taskId)}`) },
        } : undefined);
      } catch (err) {
        f.setError(err?.message || t('common.error'));
      } finally {
        busy = false;
        submit.disabled = false;
        submit.classList.remove('is-busy');
      }
    }
    const form = h('form', { class: 'stack ad-totask', novalidate: true, onSubmit }, f, imagesBlock, sw, modeHint);
    submit.addEventListener('click', onSubmit);
    const modal = ctx.overlay.openModal({
      title: t('ad.toTask.title'), size: 'md', className: 'ad-totask-modal', content: form, footer: [cancel, submit],
    });
    setTimeout(fitTask, 0);
    return modal;
  }

  // The reply of an async send arrives as a `chat` event {agentId, message} or {agentId, error};
  // other events (another tab, a task noted in the discussion) reload the history.
  offs.push(ctx.events.on('chat', (d) => {
    if (destroyed || (d && d.agentId && d.agentId !== agent.id)) return;
    if (d && typeof d.error === 'string' && d.error) {
      if (!awaiting && !sending) return;
      if (!sending) setAwaiting(false);
      setError(d.error); // the server's own French sentence (the message itself is stored)
      renderLog();
      return;
    }
    if (awaiting && !sending && d && d.message && appendLive(d.message)) {
      setAwaiting(false);
      renderLog();
      scrollDown();
      return;
    }
    if (sending || !loaded) return;
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => load({ quiet: true }), 400);
  }));
  // After a reconnection the reply may have been missed: read the history again.
  offs.push(ctx.events.on('hello', () => { if (!destroyed && loaded && awaiting) load({ quiet: true }); }));

  renderLog();

  return {
    el,
    footer,
    activate() {
      active = true;
      if (!loaded && !loading) load();
      else scrollDown();
      if (!forbidden && !unavailable) setTimeout(() => { if (!destroyed && active) area.focus?.({ preventScroll: true }); }, 0);
    },
    deactivate() { active = false; },
    destroy() {
      destroyed = true;
      clearTimeout(reloadTimer);
      clearTimeout(pollTimer);
      try { aborter?.abort(); } catch { /* ignore */ }
      for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
      picker.destroy();
    },
  };
}
